import { useEffect } from 'react';
import { truncate } from './seoText';

const SITE_ORIGIN = 'https://brahmoid.org';

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el && el.dataset.seo) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.dataset.seo = '1';
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"][data-seo]`);
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.dataset.seo = '1';
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

/**
 * Client-side SEO for the SPA: document title, description, canonical,
 * Open Graph/Twitter tags, and optional JSON-LD structured data (pass one
 * object, or an array to emit several — e.g. BlogPosting + BreadcrumbList).
 */
const useSeo = ({
  title,
  description,
  path,
  type = 'website',
  image,
  imageWidth,
  imageHeight,
  imageAlt,
  jsonLd,
}) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const url = path ? `${SITE_ORIGIN}${path}` : undefined;
    const imageUrl = image ? `${SITE_ORIGIN}${image}` : `${SITE_ORIGIN}/favicon.png`;
    upsertMeta('name', 'description', truncate(description));
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:site_name', 'BRAHMOID');
    upsertMeta('property', 'og:locale', 'hi_IN');
    upsertMeta('property', 'og:image', imageUrl);
    upsertMeta('property', 'og:image:width', image && imageWidth ? String(imageWidth) : undefined);
    upsertMeta('property', 'og:image:height', image && imageHeight ? String(imageHeight) : undefined);
    upsertMeta('property', 'og:image:alt', image ? imageAlt || title : undefined);
    upsertMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', imageUrl);
    upsertLink('canonical', url);

    const scripts = jsonLd
      ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean).map((obj) => {
          const s = document.createElement('script');
          s.type = 'application/ld+json';
          s.text = JSON.stringify(obj);
          document.head.appendChild(s);
          return s;
        })
      : [];

    return () => {
      document.title = prevTitle;
      scripts.forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, type, image, imageWidth, imageHeight, imageAlt, jsonLd]);
};

export default useSeo;
